import React, { useState } from 'react';
import { ArrowForward as ArrowIcon, Person as PersonIcon } from '@mui/icons-material';
import { Box, Chip, Collapse, Stack, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useCardExpansion } from '../../store/hooks';
import {
  type ActionCommand,
  ActionCommandType,
  type BaseAction,
  type CardContentProps,
  type PatientFieldOperation,
  type PatientUpdatePayload,
} from '../../types';
import { PatientField } from '../../types';
import { PatientUpdateForm } from '../edit/PatientUpdateForm';

import CardContent from './base/CardContent';

const CONTAINER_NAME = 'patient-update-card';

interface PatientUpdateCardContentProps extends CardContentProps {
  action: BaseAction<'patient_update'>;
}

const getFieldDisplayName = (operation: PatientFieldOperation): string => {
  // Use field_display_name if available, otherwise fallback to formatted field name
  if (operation.field_display_name) {
    return operation.field_display_name;
  }

  const fieldNames: Record<PatientField, string> = {
    [PatientField.FIRST_NAME]: 'First Name',
    [PatientField.LAST_NAME]: 'Last Name',
    [PatientField.EMAIL]: 'Email',
    [PatientField.PHONE]: 'Phone',
    [PatientField.ADDRESS]: 'Address',
    [PatientField.DATE_OF_BIRTH]: 'Date of Birth',
    [PatientField.GENDER]: 'Gender',
    [PatientField.EMERGENCY_CONTACT_NAME]: 'Emergency Contact Name',
    [PatientField.EMERGENCY_CONTACT_PHONE]: 'Emergency Contact Phone',
    [PatientField.INSURANCE_MEMBER_ID]: 'Insurance Member ID',
    [PatientField.INSURANCE_GROUP_ID]: 'Insurance Group ID',
  };
  return fieldNames[operation.field] || operation.field;
};

const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

const PatientUpdateCardContent: React.FC<PatientUpdateCardContentProps> = ({
  action,
  onCommand,
}) => {
  const [editedPayload, setEditedPayload] = useState<PatientUpdatePayload>(action.action_payload);
  const { isExpanded } = useCardExpansion();
  const { patient: patientMetrics } = useEventTracking();
  const expanded = isExpanded(action.action_id);

  const payload = action.action_payload;
  const operationCount = payload.operations.length;

  // Show first 2-3 operations in collapsed view
  const previewOperations = payload.operations.slice(0, 3);
  const hasMore = payload.operations.length > 3;

  const content = (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Form submitted {formatDate(payload.submitted_at)}
      </Typography>

      <Collapse in={!expanded} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 1 }}>
          <Chip
            size="small"
            label={`${operationCount} field${operationCount !== 1 ? 's' : ''} to update`}
            color="primary"
            variant="outlined"
            sx={{ mb: 1.5 }}
          />

          <Stack spacing={0.5}>
            {previewOperations.map((operation, index) => (
              <Box
                key={`${operation.field}-${index}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: '0.8125rem',
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: 'inherit', minWidth: 'fit-content' }}
                >
                  {getFieldDisplayName(operation)}:
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: 'inherit',
                    opacity: 0.7,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '80px',
                  }}
                >
                  {operation.previous_value || 'None'}
                </Typography>
                <ArrowIcon sx={{ fontSize: '12px', color: 'text.secondary', flexShrink: 0 }} />
                <Typography
                  variant="body2"
                  color="primary.main"
                  sx={{
                    fontSize: 'inherit',
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                  }}
                >
                  {operation.value}
                </Typography>
              </Box>
            ))}
            {hasMore && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}
              >
                +{payload.operations.length - 3} more changes...
              </Typography>
            )}
          </Stack>
        </Box>
      </Collapse>
    </>
  );

  const expandableContent = (
    <PatientUpdateForm payload={editedPayload} onChange={setEditedPayload} readOnly={false} />
  );

  // Use patient name if available, otherwise fallback to generic title
  const patientName = action.patient?.name;
  const title = patientName
    ? `Update ${patientName}'s Information?`
    : 'Update Patient Information?';

  return (
    <CardContent
      containerName={CONTAINER_NAME}
      id={action.action_id}
      title={title}
      content={content}
      avatarIcon={<PersonIcon />}
      onCommand={(command: ActionCommand) => {
        if (command.type === ActionCommandType.ACCEPT) {
          // Track patient update when accepted
          const fieldsUpdated = payload.operations.map(op => op.field);
          patientMetrics.updated(payload.patient_id, {
            source: 'action',
            fields_updated: fieldsUpdated,
          });
          
          if (expanded) {
            // Use ACCEPT_WITH_EDITS for edited payload
            onCommand({
              type: ActionCommandType.ACCEPT_WITH_EDITS,
              payload: editedPayload,
            });
          } else {
            onCommand({ type: ActionCommandType.ACCEPT });
          }
        } else {
          onCommand(command);
        }
      }}
      expandable={true}
      expandableContent={expandableContent}
      isExpandableContentEditable={true}
    />
  );
};

export default PatientUpdateCardContent;
